import React, { useEffect, useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import { fetchData } from '../components/misc';

const dummyData = [{
  "MONTH": "1/25",
  "SALES": 203126,
  "SPEND": 276966.90,
  "LEADS": 6,
  "AD": 108514.94,
  "BC": 24350.71,
  "Gross Margin": 70260.35,
  "CAC": 9864.99,
  "PROFIT": -73840.90,
  "DOMESTIC SPEND": 143788,
  "bills": 19162.32,
  "transaction costs": 1893.65,
  "other": 7964.64,
  "electricity": 2020,
  "groceries": 54910,
  "rent": 25412,
  "social media": 77244.38,
  "medecine": 13140,
  "debt": 0
}];

function WomenDataPie() {
  const [women, setWomen] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    fetchData('/api/sirri_api/get_women', { signal: controller.signal })
      .then(d => {
        if (d.responsecode === 200 && Array.isArray(d.acc)) {
          const exclude = [
            '__id',
            'EMAIL',
            'fullname1',
            'password',
            'confirmpassword',
            'firstname1',
            'middlename1',
            'lastname1',
            'timestamp',
            'phonenumber1',
            'phonenumbercountrycode1',
            'lastlogin',
            'last_messaged_timestamp',
            'notifications',
            'confirmedaccount',
            'futurespouse1'
          ];
          const sanitized = d.acc.map(w => {
            const obj = { ...w };
            exclude.forEach(k => delete obj[k]);
            return obj;
          });
          setWomen(sanitized);
          const keys = Object.keys(sanitized[0] || {});
          if (keys.includes('country1')) {
            setSelectedKey('country1');
          } else if (keys.length) {
            setSelectedKey(keys[0]);
          }
        }
      })
      .catch(err => console.error('Failed to load women data', err));
    return () => controller.abort();
  }, []);

  const pieData = useMemo(() => {
    if (!selectedKey) return [];
    const counts = {};
    const values = women
      .map(w => {
        let v = w[selectedKey];
        return typeof v === 'string' ? v.trim() : v;
      })
      .filter(v => v !== undefined && v !== null && v !== '');

    const numericVals = values.map(Number).filter(v => !isNaN(v));
    if (numericVals.length && numericVals.length === values.length) {
      const min = Math.min(...numericVals);
      const max = Math.max(...numericVals);
      const bins = 5;
      const step = Math.ceil((max - min + 1) / bins) || 1;
      numericVals.forEach(v => {
        const idx = Math.floor((v - min) / step);
        const start = min + idx * step;
        const end = start + step - 1;
        const key = `${start}-${end}`;
        counts[key] = (counts[key] || 0) + 1;
      });
      } else {
        women.forEach(w => {
          let val = w[selectedKey];
          if (val === undefined || val === null || val === '') {
            val = 'Unknown';
          } else if (typeof val === 'string') {
            val = val.trim();
          }
          counts[val] = (counts[val] || 0) + 1;
        });
    }
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [women, selectedKey]);

  if (!selectedKey) return null;

  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#8dd1e1', '#a4de6c', '#d0ed57', '#8884d8', '#82ca9d'];

  return (
    <div className="bg-white text-black rounded shadow p-4">
      <h2 className="font-bold mb-2">Women Data</h2>
      <div className="mb-2">
        <label htmlFor="womenkey" className="mr-2 text-sm">Key:</label>
        <select
          id="womenkey"
          className="text-black rounded p-1"
          value={selectedKey}
          onChange={e => setSelectedKey(e.target.value)}
        >
          {Object.keys(women[0] || {}).map(k => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <PieChart margin={{ top: 20, right: 40, bottom: 20, left: 20 }}>
          <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={90} label>
            {pieData.map((entry, index) => (
              <Cell key={`wk-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function MessageCountChart() {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    fetchData('/api/sirri_api/message_counts')
      .then(d => {
        if (d && d.counts) setCounts(d.counts);
      })
      .catch(err => console.error('Failed to load counts', err));
  }, []);
  const data = Object.entries(counts)
    .sort((a,b) => new Date(a[0]) - new Date(b[0]))
    .map(([date, value]) => ({ date, value }));
  if (!data.length) return null;
  return (
    <div className="bg-white text-black rounded shadow p-4">
      <h2 className="font-bold mb-2">Messages per Day</h2>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Line type="monotone" dataKey="value" stroke="#8884d8" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function PackageUpgradeCharts() {
  const [stats, setStats] = useState({ monthly: {} });

  useEffect(() => {
    const controller = new AbortController();
    fetchData('/api/sirri_api/package_upgrade_stats', { signal: controller.signal })
      .then(d => {
        if (d && d.stats) setStats(d.stats);
      })
      .catch(err => console.error('Failed to load package stats', err));
    return () => controller.abort();
  }, []);

  const packages = useMemo(() => {
    const pkgs = new Set();
    Object.values(stats.monthly).forEach(obj => {
      Object.keys(obj).forEach(p => pkgs.add(p));
    });
    return Array.from(pkgs);
  }, [stats]);

  const monthlyData = useMemo(() => (
    Object.entries(stats.monthly)
      .sort((a,b) => new Date(a[0]) - new Date(b[0]))
      .map(([period, obj]) => ({ period, ...obj }))
  ), [stats]);

  // Yearly chart removed

  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#8dd1e1', '#a4de6c', '#d0ed57'];

  if (!packages.length) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-white text-black rounded shadow p-4">
        <h2 className="font-bold mb-2">Package Upgrades Per Month</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthlyData}>
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip />
            <Legend />
            {packages.map((pkg, idx) => (
              <Bar key={pkg} dataKey={pkg} stackId="a" fill={colors[idx % colors.length]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function SalesTimelineChart({ items }) {
  const lineData = items.map(it => ({ month: it.MONTH, sales: it.SALES, spend: it.SPEND }));
  return (
    <div className="bg-white text-black rounded shadow p-4">
      <h2 className="font-bold mb-2">Sales and Spend Over Time</h2>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={lineData}>
          <XAxis dataKey="month" />
          <YAxis />
          <Tooltip />
          <Line type="monotone" dataKey="sales" stroke="#8884d8" />
          <Line type="monotone" dataKey="spend" stroke="#82ca9d" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function FinancialDashboard() {
  const [data, setData] = useState(dummyData);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch('https://n8n.tuongeechat.com/webhook/5f7df1a1-054c-42d3-8f84-89045ae34dba', { signal: controller.signal })
      .then(res => res.json())
      .then(d => {
        const allitems = d.filter(item => item.MONTH.includes('/'));
        const parseMonth = (m) => {
          const [mo, yr] = m.split('/');
          return new Date(2000 + Number(yr), Number(mo) - 1);
        };
        if (Array.isArray(allitems) && allitems.length) {
          allitems.sort((a,b) => parseMonth(a.MONTH) - parseMonth(b.MONTH));
          setData(allitems);
          setSelectedIndex(allitems.length - 1);
          setError(null);
        }
      })
      .catch(err => {
        console.error('Failed to fetch financial data', err);
        setError('Failed to fetch financial data');
      });
    return () => controller.abort();
  }, []);

  const row = data[selectedIndex];
  if (!row) return <p className="text-white">No data</p>;

  const formatNumber = (n) =>
    typeof n === 'number'
      ? n.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      : n;

  const barData = [
    { name: 'Sales', value: row.SALES },
    { name: 'Spend', value: row.SPEND }
  ];

  const pieData = [
    { name: 'Bills', value: row.bills },
    { name: 'Transaction Costs', value: row['transaction costs'] },
    { name: 'Other', value: row.other },
    { name: 'Electricity', value: row.electricity },
    { name: 'Groceries', value: row.groceries },
    { name: 'Rent', value: row.rent },
    { name: 'Social Media', value: row['social media'] },
    { name: 'Medicine', value: row.medecine },
    { name: 'Debt', value: row.debt }
  ];

  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#8dd1e1', '#a4de6c', '#d0ed57', '#8884d8', '#82ca9d'];

  return (
    <div className="p-4 text-white space-y-4">
      <h1 className="text-xl font-bold">Financial Dashboard</h1>
      <div className="mb-2">
        <label htmlFor="month" className="mr-2 text-sm">Month:</label>
        <select
          id="month"
          className="text-black rounded p-1"
          value={selectedIndex}
          onChange={e => setSelectedIndex(Number(e.target.value))}
        >
          {data.map((item, idx) => (
            <option key={idx} value={idx}>{item.MONTH}</option>
          ))}
        </select>
      </div>
      {error && <p className="text-red-500">{error}</p>}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-white text-black rounded shadow p-4">
          <div className="text-sm">Sales</div>
          <div className="text-lg font-bold">{formatNumber(row.SALES)}</div>
        </div>
        <div className="bg-white text-black rounded shadow p-4">
          <div className="text-sm">Profit</div>
          <div className="text-lg font-bold">{formatNumber(row.PROFIT)}</div>
        </div>
        <div className="bg-white text-black rounded shadow p-4">
          <div className="text-sm">Gross Margin</div>
          <div className="text-lg font-bold">{formatNumber(row['Gross Margin'])}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-white text-black rounded shadow p-4">
          <div className="text-sm">CAC</div>
          <div className="text-lg font-bold">{formatNumber(row.CAC)}</div>
        </div>
        <div className="bg-white text-black rounded shadow p-4">
          <div className="text-sm">NOC</div>
          <div className="text-lg font-bold">{formatNumber(row.NOC)}</div>
        </div>
        <div className="bg-white text-black rounded shadow p-4">
          <div className="text-sm">NOU</div>
          <div className="text-lg font-bold">{formatNumber(row.NOU)}</div>
        </div>
        <div className="bg-white text-black rounded shadow p-4">
          <div className="text-sm">New Sales</div>
          <div className="text-lg font-bold">{formatNumber(row.NEWSALES)}</div>
        </div>
        <div className="bg-white text-black rounded shadow p-4">
          <div className="text-sm">Upgrades</div>
          <div className="text-lg font-bold">{formatNumber(row.UPGRADES || row.upgrades)}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4">
        <div className="bg-white text-black rounded shadow p-4">
          <h2 className="font-bold mb-2">Sales vs Spend</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={barData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white text-black rounded shadow p-4">
          <h2 className="font-bold mb-2">Spending Breakdown</h2>
          <ResponsiveContainer width="100%" height={320}>
            <PieChart margin={{ top: 20, right: 40, bottom: 20, left: 20 }}>
              <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={90}>
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      <SalesTimelineChart items={data} />
      <MessageCountChart />
      <PackageUpgradeCharts />
      <WomenDataPie />
    </div>
  );
}
